import { reverse } from 'named-urls';
import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useModalContext } from '@fourthwall/components';
import { useCurrentShop } from '@modules/Dashboard/hooks/useCurrentShop';
import { PaymentMethodRequiredModal } from '@modules/Dashboard/modules/PaymentMethods';
import { useIsPaymentMethodModalRequiredMutation } from '@mutations/useIsPaymentMethodModalRequiredMutation';
import { routing } from '@utils/routing';
import { PaymentMethodRecommendedModal } from '../../../components/PaymentMethodRecommendedModal/PaymentMethodRecommendedModal';
import { useCreateGiftCardGroupMutation } from '../queries/useCreateGiftCardGroupMutation';
import { useCreateGiftCardMutation } from '../queries/useCreateGiftCardMutation';
import { useGiftCardGroupQuery } from '../queries/useGiftCardGroupQuery';
import { useGiftCardQuery } from '../queries/useGiftCardQuery';
import { useUpdateGiftCardGroupMutation } from '../queries/useUpdateGiftCardGroupMutation';
import { useUpdateGiftCardMutation } from '../queries/useUpdateGiftCardMutation';
export const useGiftCard = () => {
    const { giftCardId = '' } = useParams();
    const navigate = useNavigate();
    const { currentShopQuery } = useCurrentShop();
    const { open } = useModalContext();
    const isCreating = !giftCardId;
    const isGiftCardGroup = giftCardId.startsWith('gcg_');
    const { createGiftCardMutation } = useCreateGiftCardMutation();
    const { createGiftCardGroupMutation } = useCreateGiftCardGroupMutation();
    const { updateGiftCardMutation } = useUpdateGiftCardMutation();
    const { updateGiftCardGroupMutation } = useUpdateGiftCardGroupMutation();
    const { isPaymentMethodModalRequiredMutation } = useIsPaymentMethodModalRequiredMutation();
    const { giftCardQuery, invalidateGiftCardQuery } = useGiftCardQuery([{ id: giftCardId }], {
        enabled: !isCreating && !isGiftCardGroup,
    });
    const { giftCardGroupQuery, invalidateGiftCardGroupQuery } = useGiftCardGroupQuery([{ groupId: giftCardId }], {
        enabled: !isCreating && isGiftCardGroup,
    });
    const initialValues = useMemo(() => {
        if (isCreating) {
            return {
                type: 'SINGLE',
                code: '',
                codes: [],
                amount: '',
                expirationDateType: 'NONE',
                expirationDate: undefined,
            };
        }
        const expirationDate = giftCardQuery.data?.expiresAt ||
            giftCardGroupQuery.data?.giftCards?.[0]?.expiresAt ||
            undefined;
        return {
            type: isGiftCardGroup ? 'MULTIPLE' : 'SINGLE',
            code: giftCardQuery.data?.code || '',
            codes: giftCardGroupQuery.data?.giftCards?.map((giftCard) => giftCard.code) || [],
            amount: giftCardQuery.data?.balance.initial?.value?.toFixed(2) ||
                giftCardGroupQuery.data?.giftCards?.[0]?.balance.initial?.value?.toFixed(2) ||
                '',
            expirationDateType: expirationDate ? 'REQUIRED' : 'NONE',
            expirationDate: expirationDate ? new Date(expirationDate) : undefined,
        };
    }, [isCreating, isGiftCardGroup, giftCardQuery.data, giftCardGroupQuery.data]);
    const handleCreateSuccess = (giftCardId) => {
        const path = reverse(routing.promotions.giftCards.giftCard.self, {
            giftCardId,
        });
        navigate(path);
    };
    const createMultipleGiftCards = async (values) => {
        const data = await createGiftCardGroupMutation.mutateAsync([
            {
                codes: values.codes,
                amount: {
                    value: Number(values.amount),
                    currency: currentShopQuery.data?.settings.currency,
                },
                expiresAt: values.expirationDateType === 'REQUIRED'
                    ? values.expirationDate?.toISOString()
                    : undefined,
            },
        ]);
        handleCreateSuccess(data.giftCardGroupId);
    };
    const createSingleGiftCard = async (values) => {
        const data = await createGiftCardMutation.mutateAsync([
            {
                code: values.code,
                amount: {
                    value: Number(values.amount),
                    currency: currentShopQuery.data?.settings.currency,
                },
                expiresAt: values.expirationDateType === 'REQUIRED'
                    ? values.expirationDate?.toISOString()
                    : undefined,
            },
        ]);
        handleCreateSuccess(data.giftCardId);
    };
    const createGiftCards = (values, formikHelpers) => {
        formikHelpers.resetForm({ values });
        if (values.type === 'SINGLE') {
            createSingleGiftCard(values);
            return;
        }
        createMultipleGiftCards(values);
    };
    const addPaymentMethod = (values, formikHelpers) => {
        open(PaymentMethodRequiredModal, {
            title: 'Payment method required',
            subtitle: (React.createElement(React.Fragment, null,
                React.createElement("p", null, "Gift cards can incur costs when the value of the gift card exceeds your profit. You need to add a payment method to your account before you can proceed."),
                React.createElement("p", null, "Your payment method will only be charged if your account profits are not sufficient to cover the balance."))),
            onSuccess: () => {
                createGiftCards(values, formikHelpers);
            },
        });
    };
    const handlePaymentModal = (values, formikHelpers, isPaymentMethodModalRequired) => {
        if (!isPaymentMethodModalRequired) {
            createGiftCards(values, formikHelpers);
            return;
        }
        open(PaymentMethodRecommendedModal, {
            title: 'Do you want to allow promotions that result in negative profit?',
            confirmLabel: 'Create promotion',
            onConfirm: (paymentMethodChoice) => {
                if (paymentMethodChoice === 'SKIP') {
                    createGiftCards(values, formikHelpers);
                    return;
                }
                addPaymentMethod(values, formikHelpers);
            },
        });
    };
    const handleUpdateGiftCard = async (values) => {
        if (values.type === 'SINGLE') {
            await updateGiftCardMutation.mutateAsync([
                { id: giftCardId },
                {
                    expiresAt: values.expirationDateType === 'REQUIRED'
                        ? values.expirationDate?.toISOString()
                        : undefined,
                },
            ]);
        }
        if (values.type === 'MULTIPLE') {
            await updateGiftCardGroupMutation.mutateAsync([
                { groupId: giftCardId },
                {
                    expiresAt: values.expirationDateType === 'REQUIRED'
                        ? values.expirationDate?.toISOString()
                        : undefined,
                },
            ]);
        }
        invalidateGiftCardQuery();
        invalidateGiftCardGroupQuery();
    };
    const handleSubmit = (values, formikHelpers) => {
        if (isCreating) {
            isPaymentMethodModalRequiredMutation.mutate([], {
                onSuccess: (data) => {
                    handlePaymentModal(values, formikHelpers, data.isPaymentMethodModalRequired);
                },
            });
            return;
        }
        handleUpdateGiftCard(values);
    };
    return {
        giftCard: isCreating ? undefined : giftCardQuery.data || giftCardGroupQuery.data,
        initialValues,
        isCreating,
        isFetching: giftCardQuery.isFetching || giftCardGroupQuery.isFetching || currentShopQuery.isFetching,
        isSubmitting: createGiftCardMutation.isLoading ||
            createGiftCardGroupMutation.isLoading ||
            updateGiftCardMutation.isLoading ||
            updateGiftCardGroupMutation.isLoading,
        onSubmit: handleSubmit,
    };
};
